import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import api, { DEFAULT_API_URL } from './apiService'; 
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import logo_cf from './logo_cf.svg';
import logo_g2f from './logo_G2F.png';
import LateralMenu from './LateralMenu';
import Login from './Login';
import Home from './Home';
import Dashboard from './Dashboard';
import ControlPanel from './ControlPanel';
import Caller from './Caller';
import UserManagement from './UserManagement';
import Settings from './Settings';

// --- COMPONENTE: Selector de Tienda ---
function StoreSelector({ stores, onSelect, onLogout, user }) {
  return (
      <div className="container mt-5">
          <div className="row justify-content-center">
              <div className="col-md-8 col-lg-6">
                  <div className="card shadow-sm">
                      <div className="card-body text-center p-4">
                          <h3 className="card-title mb-3">Bienvenido, {user.fullname}</h3>
                          
                          {stores.length > 0 ? (
                            <>
                              <p className="card-text text-muted mb-4">Por favor, selecciona una tienda para continuar:</p>
                              <div className="list-group">
                                  {stores.map(store => (
                                      <button 
                                          type="button" 
                                          className="list-group-item list-group-item-action"
                                          key={store.id} 
                                          onClick={() => onSelect(store)}
                                      >
                                          {store.name}
                                      </button>
                                  ))}
                              </div>
                            </>
                          ) : (
                            <p className="card-text text-danger mb-4">
                              No tienes ninguna tienda asignada. Por favor, contacta a un administrador.
                            </p>
                          )}

                          <button className="btn btn-outline-secondary mt-4" onClick={onLogout}>
                              Cerrar Sesión
                          </button>
                      </div>
                  </div>
              </div>
          </div>
      </div>
  );
}

function App() {
  const [session, setSession] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('session')) || null;
    } catch (e) {
      localStorage.removeItem('session');
      return null;
    }
  });

  const setSessionWithStorage = (newSession) => {
    if (newSession) {
      localStorage.setItem('session', JSON.stringify(newSession));
    } else {
      localStorage.removeItem('session');
    }
    setSession(newSession);
  };

  useEffect(() => {
    const validateSession = async () => {
      if (session && session.access_token) {
        try {
          await api.get('/api/session');
        } catch (error) {
          console.error('Validación de sesión fallida', error);
          logout(); 
        }
      }
    };
    
    validateSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const logout = () => {
    setSessionWithStorage(null);
  };

  const handleLoginSuccess = (loginData) => {
    if (!loginData || !loginData.access_token || !loginData.user) {
        console.error("handleLoginSuccess: Faltan datos de login.");
        return; 
    }
    if (!loginData.accessible_stores || !Array.isArray(loginData.accessible_stores)) {
        console.warn("handleLoginSuccess: 'accessible_stores' inválido, usando vacío.");
        loginData.accessible_stores = []; 
    }

    const storesWithUrls = loginData.accessible_stores.map(store => ({
        ...store,
        api_url: store.api_url || DEFAULT_API_URL 
    }));

    const newSession = {
        ...loginData,
        accessible_stores: storesWithUrls,
        current_store: null 
    };
    setSessionWithStorage(newSession);
  };

  const handleStoreSelect = (store) => {
    setSessionWithStorage({
        ...session,
        current_store: store
    });
  };

  return (
    <Router>
      <div className="app-container">
        <header className="app-header">
          <div className="row" style={{ height: "9vh" }}>
            <div className="col-md-2 d-flex align-items-center"><img src={logo_cf} alt="logo_cf" id="header-image-logocf" /></div>
            <div className="col-md-8 d-flex align-items-center">
              <h1 className="header-text">
                Gestión de Cajas - {session && session.current_store ? session.current_store.name : "Bienvenido"}
              </h1>
            </div>
            <div className="col-md-2 d-flex align-items-center"><img src={logo_g2f} alt="logo_g2f" id="header-image-logog2f" /></div>
          </div>
        </header>

        <main>
          <Routes>
            {/* 1. RUTAS PÚBLICAS (Modo Kiosco) - Accesibles sin login */}
            <Route path="/caller/:storeId/:screenId" element={<Caller />} />
            <Route path="/caller/:storeId" element={<Caller />} />

            {/* 2. RUTA DE LOGIN */}
            <Route path="/login" element={!session ? <Login setSession={handleLoginSuccess} /> : <Navigate to="/" />} />

            {/* 3. RUTAS PROTEGIDAS (Requieren Sesión) */}
            <Route path="*" element={
              !session ? (
                 <Navigate to="/login" />
              ) : !session.current_store ? (
                 // Usuario logueado pero sin tienda seleccionada
                 <Routes>
                    <Route 
                      path="/select-store" 
                      element={<StoreSelector 
                        stores={session.accessible_stores} 
                        onSelect={handleStoreSelect} 
                        onLogout={logout}
                        user={session.user}
                      />} 
                    />
                    <Route path="*" element={<Navigate to="/select-store" />} />
                 </Routes>
              ) : (
                 // Usuario logueado Y con tienda seleccionada (Dashboard completo)
                 <div className="row" style={{ minHeight: "91vh" }}>
                    <div className="col-md-1" style={{ backgroundColor: "#101010" }}>
                      <LateralMenu session={session} logout={logout} />
                    </div>
                    <div className="col-md-11">
                      <Routes>
                        <Route path="/" element={<Home session={session} />} />
                        <Route path="/dashboard" element={<Dashboard session={session} />} />
                        <Route path="/control_panel" element={<ControlPanel session={session} />} />
                        
                        {/* RUTA CALLER INTERNA: Pasamos el ID de la tienda actual como prop */}
                        <Route path="/caller" element={<Caller storeIdProp={session.current_store.id} />} />
                        
                        <Route path="/users" element={<UserManagement session={session} />} />
                        <Route path="/settings" element={<Settings session={session} />} />
                        <Route path="*" element={<Navigate to="/" />} />
                      </Routes>
                    </div>
                 </div>
              )
            } />
          </Routes>
        </main>
      </div>
    </Router>
  );
}

export default App;
